import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import { bannerSurface } from "@utils/bannerGradient";

import { usePendingEmailReminder } from "@/contexts/PendingEmailReminderContext";

/**
 * Site-wide dismissible banner shown on every page when the logged-in
 * user has a pending email change waiting to be verified AND has already
 * closed the verify modal at least once (the reminder context handles
 * the "first close" gate). Click "Verify now" → `/account#email-verify`,
 * which AccountDashboard's hash handler picks up to smooth-scroll + pulse
 * the email row.
 *
 * Mounted inside `SecondHeaderContainer` so it sits below the nav row
 * within the same fixed container — otherwise the fixed header would
 * cover it on non-/account pages.
 *
 * Dismissal: sessionStorage, keyed by the actual pending-email value so
 * a new pending change re-shows the banner.
 *
 * Hidden on /account (the in-page banner above the page heading covers
 * the dashboard); shown on every other page including other /account/*
 * routes that don't have their own banner (subscription, claims, etc.).
 */
const STORAGE_KEY = "pending-email-banner-dismissed";

const PendingEmailGlobalBanner: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { shouldShowReminder, pendingEmail } = usePendingEmailReminder();
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pendingEmail) return;
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      setDismissed(stored === pendingEmail);
    } catch { /* sessionStorage blocked — show banner */ }
  }, [pendingEmail]);

  // /account is the dashboard — the in-page banner above the heading covers
  // it. Show on every other route, including /account/subscription and
  // /account/claims (which don't have their own pending-email banner).
  const show = shouldShowReminder && !dismissed
    && pathname !== "/account" && pathname !== "/account/";

  // Reserve the banner's height via the CSS var (it lives in the fixed
  // SecondHeader container) so the body padding-top pushes content down
  // below it instead of letting it slide behind — same as every other
  // global banner.
  useBannerOffset("--pending-email-banner-h", show, bannerRef);

  if (!show) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_KEY, pendingEmail!); } catch { /* noop */ }
  };

  const handleVerify = () => {
    navigate("/account#email-verify");
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        ...bannerSurface("rgb(209, 116, 50)"),
        boxSizing: "border-box",
        // -1px pulls the banner flush with the website header so the
        // hairline gap between the brown nav strip and the banner edge
        // disappears.
        marginTop: -1,
      }}
    >
      <div
        style={{
          boxSizing: "border-box",
          width: "100%",
          maxWidth: 1280,
          margin: "0 auto",
          padding: "10px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
          <span
            style={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: "0.875rem",
              color: "#fff",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Verify your new email "<strong>{pendingEmail}</strong>"
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleVerify}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "linear-gradient(135deg, #ffc275 0%, #ffe09f 100%)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)"; }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "7px 16px",
              borderRadius: 8,
              border: "none",
              background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
              color: "#2a1d14",
              fontFamily: '"Raleway", sans-serif',
              fontSize: "0.8125rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.15s ease",
              whiteSpace: "nowrap",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-10 7L2 7" />
            </svg>
            Verify now
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            className="banner-dismiss"
            onClick={handleDismiss}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255, 255, 255, 0.98)"; }}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255, 255, 255, 0.98)",
              cursor: "pointer",
              padding: 4,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "color 0.15s ease",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingEmailGlobalBanner;
